import styled, { StyledComponentPropsWithRef } from 'styled-components'

import { KeyboardEvent, RefObject, useState } from 'react'

import { theme } from 'styles/theme'
import { GhostButton, PrimarySearchIconButton, type ButtonProps } from '../Button/Button'
import { ChevronIcon, SvgIcons, TrashcanIcon } from '../Icon/Icon'
import { TabGroup } from '../TabGroup/TabGroup'
import { Label } from '../Typography/Typography'
import { svgNoShrinkClassName, svgPointerClassName } from 'styles/globalCss'
import { Column } from '../Table/Table'

const InputOuter = styled.div<WithIsDisabled & { flexGrow?: number }>`
  display: flex;
  flex-direction: column;
  text-align: start;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'text')};
  flex-grow: ${({ flexGrow = 0 }) => flexGrow};
`

const Chevron = styled(ChevronIcon)`
  transition: transform ${theme.transitions.normal} ease-out;
  pointer-events: none;
  position: absolute;
  inset-inline-end: 0;
`

type InputStyle = 'textFrame' | 'underline' | 'clear' | 'view-only'
interface InputLabelProps extends WithIsDisabled, WithIsValid {
  inputStyle: InputStyle
  inputHeight: string,
  isEditable: boolean,
}

// stylelint-disable max-nesting-depth -- this file requires a lot of nesting
const InputBox = styled.div<InputLabelProps>`
  // note that the SelectBox and InputBox are aligned in terms of default padding
  padding: 0.4em 0.5em 0.3em 0.2em;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5em;
  height: ${({ inputHeight }) => (inputHeight)};;
  width: 100%;
  font-size: ${theme.fontSizes.small};

  flex-grow: 1;

  position: relative;
  text-transform: none;
  
  color: ${({ isDisabled }) => (isDisabled ? theme.colors.Grey5 : theme.colors.body)};
  transition: all ${theme.transitions.normal};
  pointer-events: ${({ isDisabled }) => (isDisabled ? 'none' : 'all')};
  border-radius: ${theme.radii.small};

  border-color: ${({isValid}) => (isValid ? theme.colors.MediumGrey : theme.colors.RpicRed)};
  background-color: ${({ inputStyle }) => (inputStyle === 'textFrame' ? theme.colors.White : 'transparent')};

  ${TabGroup} &[class] {
    /* Inside Tab group we use a slightly different UI */
    border-color: transparent;
    background-color: transparent;
  }

  border-width: ${theme.borderWidths.thin};

  border-style: ${({ inputStyle }) =>
    inputStyle === 'textFrame'
      ? 'solid'
      : inputStyle === 'underline'
      ? 'none none solid none'
      : inputStyle === 'clear'
      ? 'none'
      : 'unset'};

  grid-gap: 1em;
  min-block-size: 50%;
  
  &[class]:hover {
    border-color: ${({ isEditable }) => (isEditable ? theme.colors.SemiDarkGrey : '')};

    ${Chevron} {
      transition: inherit;
      color: ${theme.colors.MainYellow};
    }
  }

  &[class]:focus-within {
    border-color: ${({ isEditable }) => (isEditable ? theme.colors.MainYellow : '')};
  }
  
  ${Chevron} {
    color: ${({ isValid }) => (isValid ? theme.colors.DarkGrey : theme.colors.danger)};
  }
`

const StyledLabel = styled(Label)`
  transition: all 0.3s ease-in;
  display: flex;
  flex-direction: column;
  padding: 0.1em 0.5em 0em 0.5em;
  height: 0px;
  width: 0px;
  opacity: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${theme.colors.SemiDarkGrey};
  font-weight: ${theme.fontWeights.regular};

  :not([value='']){
    transition: all ${theme.transitions.normal} ease-in-out;
    height: 11px;
    opacity: 1;
    margin: 0em 0em 0.2em 0em;
  }
`

export const StyledInput = styled.input`
  // note that the StyledSelect and StyledInput are aligned in terms of default padding
  display: flex;
  padding: 0.5em;
  inline-size: 100%;
  font-size: ${theme.fontSizes.small};
  font-weight: ${theme.fontWeights.regular};
  min-width: 3em;
  width: 100%;
  height: 100%;

  ::placeholder {
    color: ${theme.colors.Gray8};
    font-size: ${theme.fontSizes.small};
    font-weight: ${theme.fontWeights.regular};
  }
  :disabled {
    color: ${theme.colors.DarkGrey};
  }
  /* Passing a "type: 'textarea'" prop adds an "as: 'textarea'" prop and should be multiline.
     Style below is critical in that case - it causes 'rows' attribute to act as expected. */
  :is(textarea) {
    overflow-wrap: break-word;
    height: 100%;
    width: 100%;
  }
  /* Passing a "type: 'textarea'" prop adds an "as: 'textarea'" prop and should be multiline.
     Style below will not be applied in that case. */
  }
  :is([type='number']) {
    ::-webkit-inner-spin-button,
    ::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
  :is([type='date']) {
    /* For the chevron */
    margin-inline-end: 2em;
    /* Webkit styling based on https://stackoverflow.com/a/45461709/915875 */
    ::-webkit-calendar-picker-indicator {
      position: absolute;
      inset: 0;
      z-index: 1;
      inline-size: auto;
      block-size: auto;
      background-image: none;
    }
    ::-webkit-inner-spin-button,
    ::-webkit-clear-button {
      z-index: 1;
    }
    ::-webkit-datetime-edit {
      z-index: 2;
      flex: none;
    }
    ::-webkit-datetime-edit-fields-wrapper {
      :focus-within {
        color: ${theme.colors.body};
      }
    }
    ::-webkit-datetime-edit-day-field,
    ::-webkit-datetime-edit-month-field,
    ::-webkit-datetime-edit-year-field {
      :focus {
        color: ${theme.colors.White};
        background-color: var(--brand);
      }
    }
    :disabled ~ ${Chevron} {
      color: ${theme.colors.Grey5};
    }
    /* If we ever need to support input[type=date]:not([required]), here's a
    workaround to hide the clear button: https://stackoverflow.com/q/49527186/915875 */
  }
  :is([type='search']) {
    ::-webkit-search-cancel-button {
      display: none;
    }
  }
`

export const ValidationMessageList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.6em;
  color: ${theme.colors.danger};
  font-size: ${theme.fontSizes.small};
  font-weight: ${theme.fontWeights.regular};
  :not(:empty) {
    padding: 0.8em;
    padding-inline-start: 0;
  }
`

const ValueText = styled.span`
  color: ${theme.colors.MainBlack};
  font-weight: ${theme.fontWeights.regular};
`

const _StyledTextArea = styled.textarea``

interface InputProps
  extends MaybeWithIsDisabled,
    MaybeWithLabel,
    MaybeWithTitle,
    MaybeWithIsRequired,
    MaybeWithValidationMessages,
    StyledComponentPropsWithRef<typeof StyledInput>
    {
  name: string
  value: string
  className?: string
  
  inputRef?: RefObject<HTMLInputElement>
  inputStyle?: InputStyle
  inputHeight?: string
  isEditable?: boolean
  isValid?: boolean
  outerFlexGrow?: number

  textareaRows?: number
}

const stopEventPropagation = (e: React.MouseEvent) => { e.stopPropagation() }
function Input({
  label,
  value,
  title = label,
  children,
  inputRef,
  type = 'text',
  inputHeight = '4em',
  showLabel = true,
  isRequired = true,
  isDisabled = false,
  validationMessages = [],
  isValid = true,
  inputStyle = 'textFrame',
  className = '',
  outerFlexGrow = 0,
  isEditable = true,
  textareaRows = undefined, // relevant for type = 'textarea'
  ...props
}: InputProps) {
  const isTextarea = type === 'textarea'
  return (
    <InputOuter {...{ title, className, isDisabled, isValid, ...(isTextarea && { style: { flexGrow: 1 }}), flexGrow: outerFlexGrow, }}>
      { 
        inputStyle === 'view-only' ? 
          <ValueText {...{ title: value, ...props }}> { value }</ValueText> :
          <InputBox {...{ isDisabled, isValid, inputStyle, inputHeight, isEditable }}>
            {children}
            <Column {...{style:{gap: '0', width: '100%', height: '100%'}}} >
              {
                showLabel && <StyledLabel {...{value: value}}>{label}</StyledLabel>
              }
              {
                isTextarea ? 
                  <StyledInput {...{ as: 'textarea', value, rows: textareaRows, disabled: isDisabled, required: isRequired, onClick: stopEventPropagation, ...(props as StyledComponentPropsWithRef<typeof _StyledTextArea>)}} /> :
                  <StyledInput {...{ ref: inputRef, type, value, disabled: isDisabled, required: isRequired, onClick: stopEventPropagation, placeholder: label, ...props }} />
              }
            </Column>
          </InputBox>
      }
      <ValidationMessageList>
        {validationMessages.map((message) => (
          <li {...{ key: message, children: message }} />
        ))}
    </ValidationMessageList> 
    </InputOuter>
  )
}

export const TextInput = styled(Input)<{ flexGrow?: number, isEditable?: boolean }>`
  /* https://stackoverflow.com/a/66689926/915875 */
  min-inline-size: 0;
  text-transform: none;
  font-weight: ${theme.fontWeights.regular};
  flex-grow: ${({ flexGrow }) => (flexGrow || 0)};
`

export type InputType = typeof Input

export const InputWithButton = styled(({ isDisabled = false, ...props }: InputProps) => (
  <TextInput {...{ isDisabled, ...props }} />
))`
  ${InputBox} {
    gap: 1em;
    flex-direction: row;
    justify-content: space-between;
    padding-inline-end: inherit;
  }
  ${StyledInput}[class][class] {
    inline-size: 100%;
    margin: auto;
    ::placeholder {
      text-transform: uppercase;
      font-weight: ${theme.fontWeights.regular};
    }
    :focus {
      margin: inherit;
      block-size: auto;
      ::placeholder {
        opacity: 0;
      }
    }
  }
`

export function PasswordInput(props: InputProps) {
  return <TextInput {...{ type: 'password', ...props }} />
}

export type PasswordInputType = typeof PasswordInput

export type InputWithButtonType = typeof InputWithButton

interface InputWithButtonProps extends Omit<InputProps, 'onClick'>, Required<Pick<ButtonProps, 'onClick'>> {
  buttonText: string
}

export function SearchInputWithSearchButton({
  buttonText,
  onClick,
  isDisabled = false,
  ...props
}: InputWithButtonProps) {
  return (
    <InputWithButton {...{ type: 'search', enterKeyHint: 'search', isDisabled, ...props }}>
      <PrimarySearchIconButton {...{ text: buttonText, onClick, isDisabled }} />
    </InputWithButton>
  )
}

interface SearchInputProps extends InputProps {
  onSearch?: () => void
  onSearchCleared?: () => void
  outerFlexGrow?: number,
  withClear?: boolean,
}

const SearchIconContainer = styled.div`
  display: flex;
  alignItems: center;
  justify-content: space-around;
  min-width: 24px;
`

type SearchActionIcon = 'search' | 'clear'
// eslint-disable-next-line import/no-unused-modules
export function SearchInput({ 
  isDisabled = false, 
  onSearch, 
  onSearchCleared, 
  outerFlexGrow,
  withClear = true,
  ...props
}: SearchInputProps) {
  const [icon, setIcon] = useState<SearchActionIcon>('search')
  const setIconByValue = () => {
    if (!withClear) { return }

    setIcon(props.value === '' ? 'search' : 'clear')
  }

  return (
    <Input
      {...{
        type: 'search',
        enterKeyHint: 'search',
        isDisabled,
        onFocus: () => {
          setIconByValue()
        },
        onBlur: () => {
          onSearch?.()
          setIcon('search')
        },
        outerFlexGrow,
        onSubmit: onSearch,
        onKeyUp: (event: KeyboardEvent<HTMLInputElement>) => {
          // TODO: change this crap
          if (event.key === 'Enter') {
            onSearch?.()
          }
          setIconByValue()
        },
        ...props,
      }}
    >
      <SearchIconContainer>
        {icon === 'search' ? (
          <SvgIcons.MagnifyingGlassSvg
            className={svgNoShrinkClassName}
            onClick={() => {
              onSearch?.()
            }}
          />
        ) : (
          // onMouseDown instead of onClick to make it run before onBlur of the parent. Not the most elegant solution, but it's the simplest.
          // https://stackoverflow.com/questions/17769005/onclick-and-onblur-ordering-issue
          <SvgIcons.CancelCrossSvg
            className={`${svgNoShrinkClassName} ${svgPointerClassName}`}
            onMouseDown={() => {
              onSearchCleared?.()
            }}
          />
        )}
      </SearchIconContainer>
    </Input>
  )
}

export type SearchInputWithSearchButtonType = typeof SearchInputWithSearchButton

export function EmailInputWithDeleteButton({
  buttonText,
  onClick,
  isDisabled = false,
  ...props
}: InputWithButtonProps) {
  return (
    <InputWithButton {...{ type: 'email', enterKeyHint: 'done', isDisabled, ...props }}>
      <GhostButton {...{ text: buttonText, onClick, isDisabled, startComponent: <TrashcanIcon /> }} />
    </InputWithButton>
  )
}

export type EmailInputWithDeleteButtonType = typeof EmailInputWithDeleteButton
